import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {
  FooterContainer,
  FooterContent,
  TopSection,
  SocialLinks,
  SocialButton,
  LinkGrid,
  NavColumn,
  ColumnTitle,
  NavLink,
  NavActionButton,
  AskAiSection,
  AskAiTitle,
  AskAiLinks,
  AskAiButton,
  BottomRow,
  FooterEnd,
  Copyright,
  CopyrightGroup,
  BackedBy,
} from '../../styles/shared/FooterStyles';
import ViewAsAgentButton from './ViewAsAgentButton';
import XIcon from '../icons/XIcon';
import AskAiIcon from '../icons/AskAiIcon';
import tiktokIcon from '../../assets/images/icons/tiktok.png';
import { MILLET_SOCIAL_LINKS } from '../../constants/milletSocialLinks';
import { MILLET_ASK_AI_LINKS } from '../../constants/milletAskAiLinks';
import { dispatchOpenCookieSettings } from '../../utils/cookies/cookieConsent';
import {
  MOBILE_BOTTOM_NAV_MQL,
  isCampaignDetailRoute,
  isInfluencerDetailRoute,
  shouldShowBottomNavigation,
} from '../../utils/navigation/bottomNavigationVisibility';
import useHideCreatorMapEntryPoints from '../../hooks/navigation/useHideCreatorMapEntryPoints';
import { CREATOR_MAP_PATH } from '../../utils/navigation/creatorMapVisibility';
import {
  AuthenticatedLandingFooterHairline,
  GuestLandingFooterDots,
  isGuestDocumentFramePath,
} from '../../pages/LandingPage/styles/marketingLandingStyles';

const SOCIAL_ICON_SIZE = 20;
const ASK_AI_ICON_SIZE = 14;

const FOOTER_COLUMNS = ['Product', 'Legal', 'Resources', 'Company'];

const socialIconById = {
  linkedin: <LinkedInIcon sx={{ fontSize: SOCIAL_ICON_SIZE }} />,
  x: <XIcon size={SOCIAL_ICON_SIZE} />,
  tiktok: (
    <img
      src={tiktokIcon}
      alt=""
      aria-hidden
      width={SOCIAL_ICON_SIZE}
      height={SOCIAL_ICON_SIZE}
      decoding="async"
    />
  ),
};

const getNavigationSections = (
  isAuthenticated,
  userType,
  { includeCreatorMap = true } = {},
) => {
  const sections = {
    Product: [],
    Resources: [],
    Company: [],
    Legal: [
      { text: 'Privacy Policy', to: '/privacy-policy' },
      { text: 'Terms of Service', to: '/terms-and-conditions' },
      { text: 'Cookie settings', action: 'cookie-settings' },
    ],
  };

  if (!isAuthenticated || userType === 'brand') {
    if (includeCreatorMap) {
      sections.Product.push({ text: 'Influencer Map', to: CREATOR_MAP_PATH });
    }
    sections.Product.push(
      { text: 'Browse Influencers', to: '/marketplace/influencers' },
    );
  } else if (userType === 'influencer') {
    sections.Product.push(
      { text: 'Campaigns', to: '/marketplace/campaigns' },
      { text: 'Browse Brands', to: '/marketplace/campaigns#brand-shelves' },
    );
  }

  if (!isAuthenticated || userType === 'brand') {
    sections.Product.push({ text: 'Pricing', to: '/pricing' });
  }

  sections.Resources.push(
    { text: 'FAQ', to: '/faq' },
    { text: 'Blog', to: '/blog' },
  );
  if (!isAuthenticated) {
    sections.Resources.push({ text: 'Dashboard', to: '/sign-up' });
  } else if (userType === 'brand') {
    sections.Resources.push(
      { text: 'Dashboard', to: '/dashboard/overview' },
      { text: 'Campaigns', to: '/dashboard/campaigns' },
      { text: 'Profile', to: '/dashboard/profile' },
    );
  } else {
    sections.Resources.push(
      { text: 'Dashboard', to: '/dashboard/overview' },
      { text: 'Collaborations', to: '/dashboard/collaborations' },
      { text: 'Profile', to: '/dashboard/profile' },
    );
  }

  sections.Company.push(
    { text: 'Contact Us', to: '/contact-us' },
  );

  return sections;
};

function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const userType = useSelector((state) => state.auth.userType);
  const isMobileNavBreakpoint = useMediaQuery(MOBILE_BOTTOM_NAV_MQL);
  const hideCreatorMapEntryPoints = useHideCreatorMapEntryPoints();
  const needsStickyCtaClearance = isMobileNavBreakpoint && (
    isInfluencerDetailRoute(location.pathname)
    || isCampaignDetailRoute(location.pathname)
  );
  const needsBottomNavClearance = !needsStickyCtaClearance
    && isMobileNavBreakpoint
    && shouldShowBottomNavigation({
      pathname: location.pathname,
      isAuthenticated,
      userType,
    });
  const navigationSections = getNavigationSections(
    isAuthenticated,
    userType,
    { includeCreatorMap: !hideCreatorMapEntryPoints },
  );
  const currentYear = new Date().getFullYear();

  const handleLinkClick = (e, to) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    navigate(to);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  const isMarketplacePage = location.pathname === '/marketplace'
    || location.pathname.startsWith('/marketplace/');
  const isGuestDocumentPage = isGuestDocumentFramePath(location.pathname);
  // FAQ, privacy, and terms keep the guest document column, so the closing
  // rule meets the CTA box on that gutter when the viewer is signed in.
  const showGuestFooterDots = isGuestDocumentPage
    || (location.pathname === '/' && !isAuthenticated);
  const signedInLandingFooter = isAuthenticated && location.pathname === '/';

  return (
    <FooterContainer $guestFrame={showGuestFooterDots} $signedInHome={signedInLandingFooter}>
      {showGuestFooterDots ? <GuestLandingFooterDots /> : null}
      {signedInLandingFooter ? <AuthenticatedLandingFooterHairline /> : null}
      <FooterContent
        $marketplace={isMarketplacePage}
        $clearStickyCta={needsStickyCtaClearance}
        $clearBottomNav={needsBottomNavClearance}
        data-clear-bottom-nav={needsBottomNavClearance ? 'true' : 'false'}
        data-clear-sticky-cta={needsStickyCtaClearance ? 'true' : 'false'}
      >
        <TopSection>
          <LinkGrid>
            <AskAiSection>
              <AskAiTitle>Ask AI about Millet</AskAiTitle>
              <AskAiLinks>
                {MILLET_ASK_AI_LINKS.map((link) => (
                  <AskAiButton
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Ask ${link.name} about Millet`}
                  >
                    <AskAiIcon id={link.id} size={ASK_AI_ICON_SIZE} />
                  </AskAiButton>
                ))}
              </AskAiLinks>
            </AskAiSection>
            {FOOTER_COLUMNS.map((columnTitle) => {
              const links = navigationSections[columnTitle] || [];
              if (!links.length) return null;

              return (
                <NavColumn key={columnTitle}>
                  <ColumnTitle>{columnTitle}</ColumnTitle>
                  {links.map((link) => {
                    if (link.action === 'cookie-settings') {
                      return (
                        <NavActionButton
                          key={link.action}
                          type="button"
                          onClick={dispatchOpenCookieSettings}
                        >
                          {link.text}
                        </NavActionButton>
                      );
                    }

                    return (
                      <NavLink
                        key={link.to}
                        href={link.to}
                        onClick={(e) => handleLinkClick(e, link.to)}
                      >
                        {link.text}
                      </NavLink>
                    );
                  })}
                </NavColumn>
              );
            })}
          </LinkGrid>
        </TopSection>

        <BottomRow>
          <CopyrightGroup>
            <Copyright>
              ©
              {' '}
              {currentYear}
              {' '}
              Millet Inc.
            </Copyright>
            <BackedBy
              href="https://launch.co"
              target="_blank"
              rel="noopener noreferrer"
            >
              Backed by LAUNCH CO.
            </BackedBy>
          </CopyrightGroup>
          <FooterEnd>
            <SocialLinks>
              {MILLET_SOCIAL_LINKS.map((link) => (
                <SocialButton
                  key={link.id}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.name}
                  $brandColor={link.brandColor}
                >
                  {socialIconById[link.id]}
                </SocialButton>
              ))}
            </SocialLinks>
            <ViewAsAgentButton />
          </FooterEnd>
        </BottomRow>
      </FooterContent>
    </FooterContainer>
  );
}

export default Footer;
